import React, { useState } from "react";
import styled from "styled-components";
import { 
  FaUser, 
  FaUserTag, 
  FaShieldAlt, 
  FaUsers,
  FaBars,
  FaTimes
} from "react-icons/fa";
import Profile from "./Profile.js";
import Designation from "./Designation.js";
import Permission from "./Permission.js";
import User from "./User.js";
import "./style.css";

// Styled Components
const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const TabsContainer = styled.nav`
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 90;
  
  @media (max-width: 767px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    top: auto;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    border-top: 1px solid #e9ecef;
  }
`;

const TabsWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
`;

const Tabs = styled.div`
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 767px) {
    justify-content: space-around;
    padding: 0.5rem 0;
  }
`;

const TabButton = styled.button`
  background: transparent;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6c757d;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  min-width: fit-content;
  
  svg {
    font-size: 1rem;
  }
  
  &:hover {
    color: #0d6efd;
    background: rgba(13, 110, 253, 0.05);
  }
  
  &.active {
    color: #0d6efd;
    background: rgba(13, 110, 253, 0.1);
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
      border-radius: 3px 3px 0 0;
    }
  }
  
  @media (min-width: 768px) {
    padding: 1.25rem 2rem;
    font-size: 1rem;
    
    svg {
      font-size: 1.1rem;
    }
  }
  
  @media (max-width: 767px) {
    flex: 1;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    min-width: 0;
    
    svg {
      font-size: 1.2rem;
    }
    
    &.active::after {
      top: 0;
      bottom: auto;
      height: 3px;
      border-radius: 0 0 3px 3px;
    }
  }
`;

const ContentContainer = styled.main`
  padding: 0;
  min-height: calc(100vh - 70px);
  
  @media (max-width: 767px) {
    min-height: calc(100vh - 80px);
    padding-bottom: 80px; /* Space for bottom tabs */
  }
`;

// Tab configuration with icons
const tabConfig = [
  { id: "profile", label: "Profile", icon: FaUser },
  { id: "designation", label: "Designation", icon: FaUserTag },
  { id: "permission", label: "Permission", icon: FaShieldAlt },
  { id: "user", label: "User", icon: FaUsers }
];

function App() {
  const [activeTab, setActiveTab] = useState("profile");

  const renderContent = () => {
    switch (activeTab) {
      case "profile":
        return <Profile />;
      case "designation":
        return <Designation />;
      case "permission":
        return <Permission />;
      case "user":
        return <User />;
      default:
        return <Profile />;
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  return (
    <AppContainer>
      <TabsContainer>
        <TabsWrapper>
          <Tabs>
            {tabConfig.map(({ id, label, icon: Icon }) => (
              <TabButton
                key={id}
                onClick={() => handleTabClick(id)}
                className={activeTab === id ? "active" : ""}
              >
                <Icon />
                {label}
              </TabButton>
            ))}
          </Tabs>
        </TabsWrapper>
      </TabsContainer>

      <ContentContainer>
        {renderContent()}
      </ContentContainer>
    </AppContainer>
  );
}

export default App;